import React from "react";
import { Activity, Dumbbell, Zap, Target, type LucideIcon } from "lucide-react";
import type { Lang } from "@/contexts/LanguageContext";

/**
 * Central translation dictionary for the public site.
 * Spanish is the source language and the default; English is a full translation.
 * Legal pages (Privacy / Legal / Cookies) intentionally remain Spanish-only.
 */

export interface Pillar {
  id: string;
  icon: LucideIcon;
  number: string;
  title: string;
  subtitle: string;
  description: string;
  tags: string[];
}

export interface TechItem {
  name: string;
  role: string;
  description: string;
}

export interface Testimonial {
  name: string;
  role: string;
  photo: string;
  quote: string;
}

export interface Faq {
  q: string;
  a: string;
}

interface Dict {
  nav: { metodologia: string; tecnologia: string; equipo: string; colaboradores: string; testimonios: string; faq: string; contacto: string };
  header: { adminPanel: string; cta: string; menu: string; home: string; langLabel: string };
  hero: {
    badge: string;
    titleLine1: string;
    titleLine2: string;
    titleLine3: string;
    paragraph: React.ReactNode;
    ctaPrimary: string;
    ctaSecondary: string;
    labChip: string;
    heroImgAlt: string;
  };
  stats: { num: string; label: string }[];
  methodology: {
    eyebrow: string;
    titleA: string;
    titleEm: string;
    titleB: string;
    intro: string;
    imgAlt: string;
    imgCaption: string;
    processImgAlt: string;
    processTitleA: string;
    processTitleEm: string;
    processTitleB: string;
    processIntro: string;
    steps: { step: string; title: string; text: string }[];
  };
  pillars: Pillar[];
  technology: {
    eyebrow: string;
    titleA: string;
    titleEm: string;
  };
  tech: TechItem[];
  team: {
    eyebrow: string;
    titleA: string;
    titleEm: string;
    groupAlt: string;
    followInstagram: string;
    teaserIntro: string;
    teaserCta: string;
    pageIntro: string;
    backHome: string;
  };
  quote: string;
  collaborators: {
    eyebrow: string;
    title: string;
    intro: string;
  };
  testimonialsSection: {
    eyebrow: string;
    titleA: string;
    titleEm: string;
  };
  testimonials: Testimonial[];
  faqSection: {
    eyebrow: string;
    titleA: string;
    titleEm: string;
    intro: string;
    helpBox: string;
    helpLink: string;
  };
  faqs: Faq[];
  contact: {
    eyebrow: string;
    titleA: string;
    titleEm: string;
    intro: string;
    emailLabel: string;
    whatsappLabel: string;
    locationLabel: string;
    locationValue: string;
    mapTitle: string;
    form: {
      name: string;
      email: string;
      phone: string;
      handicap: string;
      handicapPlaceholder: string;
      playerProfile: string;
      playerProfilePlaceholder: string;
      profiles: { estancado: string; dolor: string; competidor: string; profesional: string; otro: string };
      message: string;
      messagePlaceholder: string;
      consentPre: string;
      consentLink: string;
      consentPost: string;
      submit: string;
      submitting: string;
      errorIncomplete: string;
      success: string;
      errorSend: string;
    };
  };
  footer: {
    brandText: string;
    navTitle: string;
    contactTitle: string;
    legalTitle: string;
    nav: { metodologia: string; tecnologia: string; equipo: string; contacto: string };
    legal: { privacidad: string; aviso: string; cookies: string };
    rights: string;
  };
  cookies: {
    eyebrow: string;
    title: string;
    body: React.ReactNode;
    accept: string;
    reject: string;
    more: string;
    ariaDialog: string;
    ariaClose: string;
    policyLink: string;
  };
  whatsapp: { aria: string };
}

const es: Dict = {
  nav: {
    metodologia: "Metodología",
    tecnologia: "Tecnología",
    equipo: "Equipo",
    colaboradores: "Colaboradores",
    testimonios: "Testimonios",
    faq: "FAQ",
    contacto: "Contacto",
  },
  header: {
    adminPanel: "Panel admin",
    cta: "Pide tu cita",
    menu: "Menú",
    home: "Golf Motion Lab — Inicio",
    langLabel: "Idioma",
  },
  hero: {
    badge: "Valoración integral del golfista",
    titleLine1: "Conectamos",
    titleLine2: "cuerpo y swing",
    titleLine3: "para mejorar tu juego.",
    paragraph: (
      <>
        Medimos <strong className="text-[var(--forest-deep)]">movilidad</strong>,{" "}
        <strong className="text-[var(--forest-deep)]">fuerza</strong>,{" "}
        <strong className="text-[var(--forest-deep)]">rendimiento</strong> y{" "}
        <strong className="text-[var(--forest-deep)]">control motor</strong>. Traducimos los datos en
        un informe integral con puntuaciones del 0 al 100 y un plan de entrenamiento que prioriza lo
        que realmente limita tu juego.
      </>
    ),
    ctaPrimary: "Reserva tu valoración",
    ctaSecondary: "Descubre la metodología",
    labChip: "Lab · 2026",
    heroImgAlt:
      "Laboratorio Golf Motion Lab con lona corporativa, radar TrackMan, hierro y bolas Titleist sobre alfombra de práctica",
  },
  stats: [
    { num: "4", label: "Pilares de valoración" },
    { num: "20+", label: "Métricas por informe" },
    { num: "0 — 100", label: "Sistema de puntuación" },
    { num: "3", label: "Especialistas por valoración: médico, fisio y coach" },
  ],
  methodology: {
    eyebrow: "Metodología",
    titleA: "Cuatro pilares que ",
    titleEm: "deciden tu juego",
    titleB: ".",
    intro:
      "Cada pilar se puntúa sobre 100 con una escala clínica. La puntuación global es la media ponderada de los cuatro y te dice, con un único vistazo, dónde estás y dónde tienes que trabajar.",
    imgAlt:
      "Análisis biomecánico Golf Motion Lab: jugador con sensores corporales y esqueleto 3D en pantalla mostrando métricas de pelvis, torso y XFactor",
    imgCaption: "Valoración integral · Sesión real en lab",
    processImgAlt: "El equipo de Golf Motion Lab analizando los datos de una valoración en el laboratorio",
    processTitleA: "Un proceso de ",
    processTitleEm: "tres pasos",
    processTitleB: ".",
    processIntro:
      "Cada valoración se trabaja en equipo: combinamos la mirada del fisioterapeuta, el médico y el coach para que ningún dato se quede sin interpretar y el plan parta de una lectura completa de tu juego.",
    steps: [
      {
        step: "01",
        title: "Valoramos",
        text: "Sesión de 90 minutos en el laboratorio con batería completa de tests físicos, biomecánicos y de impacto.",
      },
      {
        step: "02",
        title: "Analizamos",
        text: "Cruzamos los datos de los 4 pilares y generamos un informe visual con puntuación y prioridades claras.",
      },
      {
        step: "03",
        title: "Entrenamos",
        text: "Plan de entrenamiento individualizado adaptado a tus puntos débiles. Lo puedes trabajar de forma autónoma, con tu profesional de confianza o con nuestro seguimiento.",
      },
    ],
  },
  pillars: [
    {
      id: "movilidad",
      icon: Activity,
      number: "01",
      title: "Movilidad",
      subtitle: "¿Tiene tu cuerpo el recorrido necesario?",
      description:
        "Evaluamos los rangos articulares críticos para el swing: rotación torácica, caderas, hombros y tobillos. Medimos amplitud, asimetrías y calidad del control final de rango.",
      tags: ["Toe-Touch", "Thoracic Rotation", "Hip IR/ER", "Shoulder Mobility"],
    },
    {
      id: "fuerza",
      icon: Dumbbell,
      number: "02",
      title: "Fuerza",
      subtitle: "¿Tu cuerpo aguanta y transmite velocidad?",
      description:
        "Con sistema VALD Performance cuantificamos fuerza máxima, potencia, asimetrías entre hemicuerpos y capacidad de salto. Traducimos números en objetivos entrenables.",
      tags: ["Grip Strength", "CMJ", "Isquios/Cuádriceps", "Asimetrías"],
    },
    {
      id: "rendimiento",
      icon: Zap,
      number: "03",
      title: "Rendimiento",
      subtitle: "¿Cómo se comporta tu impacto?",
      description:
        "Con TrackMan analizamos velocidad de palo, velocidad de bola, smash factor, ángulo de ataque y dispersión. Contrastamos los datos de impacto con los físicos.",
      tags: ["Club Speed", "Ball Speed", "Smash Factor", "Dispersión"],
    },
    {
      id: "control",
      icon: Target,
      number: "04",
      title: "Control motor",
      subtitle: "¿Mueves bien lo que tienes?",
      description:
        "Con OnForm y vídeo 2D+3D evaluamos secuencia cinemática, postura, equilibrio, tempo y repetibilidad. Este pilar diferencia al jugador consistente del irregular.",
      tags: ["Sequencing", "Posture", "Balance", "Tempo Ratio"],
    },
  ],
  technology: {
    eyebrow: "Tecnología clínica",
    titleA: "Las mismas herramientas que usan los ",
    titleEm: "profesionales del PGA Tour",
  },
  tech: [
    {
      name: "VALD Performance",
      role: "Fuerza, potencia y asimetrías",
      description:
        "Dinamómetros y plataformas de fuerza con precisión clínica para medir la transferencia física al swing.",
    },
    {
      name: "TrackMan",
      role: "Datos de impacto y vuelo de bola",
      description:
        "Radar doppler de referencia PGA Tour para cuantificar la eficiencia de cada golpe con objetividad absoluta.",
    },
    {
      name: "OnForm",
      role: "Análisis de vídeo y secuencia",
      description:
        "Software de análisis 2D y 3D con mediciones biomecánicas para descomponer la calidad del swing por fases.",
    },
  ],
  team: {
    eyebrow: "Equipo",
    titleA: "Profesionales que ",
    titleEm: "integran ciencia y juego",
    groupAlt: "Equipo de Golf Motion Lab: Sergio Sánchez, Aitor Pardo y Gaspar Plaza",
    followInstagram: "Sígueme en Instagram",
    teaserIntro:
      "Un médico, un fisioterapeuta y un coach trabajando sobre la misma valoración. Conoce a las personas que están detrás de cada informe.",
    teaserCta: "Conoce al equipo",
    pageIntro:
      "Detrás de cada valoración hay tres disciplinas que se cruzan: diagnóstico médico, readaptación física y análisis técnico del swing. Estas son las personas que las integran.",
    backHome: "Volver al inicio",
  },
  quote: "\"Una métrica solo entra en el informe si cambia una decisión.\"",
  collaborators: {
    eyebrow: "Colaboradores",
    title: "Trabajamos con quienes marcan el estándar.",
    intro:
      "Contamos con el respaldo de tecnología de referencia y de espacios de juego de primer nivel para que cada valoración sea rigurosa y aplicable.",
  },
  testimonialsSection: {
    eyebrow: "Testimonios",
    titleA: "Lo que dicen quienes ",
    titleEm: "ya lo han probado",
  },
  testimonials: [
    {
      name: "Rubén Pardo",
      role: "Golfista profesional · ALPS Tour / PGA Spain",
      photo: "/img/testimonial-ruben-putt_4cc510a4.jpg",
      quote:
        "Jugaba con dolor lumbar constante, torneo tras torneo, tirando de antiinflamatorios para poder competir. Fui a ver a Sergio para hacer una valoración completa y ver qué estaba pasando, y empezamos a trabajar en ello: gestión de la carga, trabajo postural y físico específico. Sin más misterio. El dolor fue remitiendo y fui ganando conciencia de cómo me muevo. Al final eso se nota en el campo.",
    },
    {
      name: "Juanjo Garrido",
      role: "Amateur · hándicap 5.3",
      photo: "/img/testimonial-juanjo_795ba4c4.jpg",
      quote:
        "El dolor lumbar me limitaba no solo en el golf, sino en mi día a día. Tras la valoración de movimiento entendí por qué me dolía. El plan de trabajo fue claro, los resultados también. Fui muy constante con lo que me pautaron y ese dolor quedó atrás.",
    },
    {
      name: "Álvaro Calero",
      role: "Golfista profesional · ALPS Tour / PGA Spain",
      photo: "/img/testimonial-alvaro-drive_e5cf6e12.jpg",
      quote:
        "Desde que empecé a trabajar con Aitor siento que afronto la competición de una manera completamente diferente. Más preparado, más seguro. Tiene una capacidad única para hacer que lo difícil parezca sencillo y para que tú lo veas también.",
    },
  ],
  faqSection: {
    eyebrow: "Preguntas frecuentes",
    titleA: "Antes de cambiar tu swing,",
    titleEm: " entiende tu cuerpo.",
    intro:
      "Resolvemos las dudas más habituales de jugadores amateurs, golfistas con dolor recurrente, competidores que buscan ganancias marginales y profesionales que quieren externalizar el diagnóstico físico de sus alumnos.",
    helpBox:
      "¿Tu pregunta no está aquí? Escríbenos por WhatsApp o desde el formulario de contacto y te respondemos en menos de 48 horas con disponibilidad de agenda y precio.",
    helpLink: "Ir al formulario de contacto",
  },
  faqs: [
    {
      q: "Llevo años atascado en el mismo hándicap, ¿qué va a cambiar después de una valoración con vosotros?",
      a: "En la mayoría de jugadores estancados el techo no está en la técnica, sino en lo que el cuerpo deja o no deja hacer al swing. Lo que sale del laboratorio no es una clase, sino un mapa: dos o tres límites físicos concretos (por ejemplo rotación torácica corta, asimetría de fuerza en cadera o secuencia descoordinada) y cómo se relacionan con tus fallos repetidos. A partir de ahí, tú y tu coach trabajáis sobre causas, no sobre síntomas. Esa diferencia es la que normalmente desbloquea el progreso.",
    },
    {
      q: "Llevo tiempo jugando con molestias lumbares o codo de golfista, ¿vuestra valoración me sirve aunque ya esté tratando el dolor?",
      a: "Sí, y de hecho es justo cuando más aporta. El golf concentra carga en lumbar, codo y hombro a través de gestos muy repetitivos; cuando duele, casi siempre hay una zona vecina que no está colaborando y está obligando a otra a sobrecargarse. Nuestra valoración no sustituye al tratamiento médico ni al fisioterapéutico: lo complementa. Aportamos datos objetivos sobre qué estructura está fallando en el swing para que tu equipo clínico pueda dirigir la rehabilitación con criterio deportivo y no solo sintomático.",
    },
    {
      q: "Quiero ganar velocidad y distancia. ¿Hasta qué punto puede ayudarme un análisis biomecánico?",
      a: "La velocidad de palo es el resultado de una cadena: cuánta fuerza eres capaz de aplicar contra el suelo, cómo la transfieres por la pelvis y el tronco, y a qué velocidad llegas al impacto sin perder eficiencia. Nosotros medimos cada eslabón con TrackMan, VALD y vídeo, y comparamos tus números con referencias deportivas. Así sabemos si tu margen de mejora está en fuerza, en coordinación, en movilidad o en técnica pura, y te decimos dónde invertir las próximas semanas para ganar metros reales sin pagar el precio en forma de lesión.",
    },
    {
      q: "¿Qué incluye exactamente la sesión y cómo es el día?",
      a: "Aproximadamente 90 minutos en el laboratorio repartidos en cuatro bloques: entrevista y revisión de tu historial deportivo, valoración física (movilidad, fuerza y asimetrías con VALD), análisis de impacto con TrackMan y análisis de swing con vídeo 2D+3D en OnForm. En los días siguientes recibes el informe completo con tus puntuaciones en los cuatro pilares y un plan de entrenamiento priorizado por orden de impacto, adaptado a tus puntos débiles.",
    },
    {
      q: "¿Qué tengo que traer y qué nivel de juego se necesita?",
      a: "Solo necesitas tu hierro medio (un hierro 7 es perfecto) y, si te interesa analizar también el driver, tráelo. Lleva ropa deportiva cómoda en la que puedas correr, saltar y rotar sin restricciones; el calzado, mejor de pista o running para los tests físicos. El resto del material lo ponemos nosotros. En cuanto al nivel: trabajamos con principiantes, jugadores de club, competidores amateurs y profesionales. Lo que cambia entre uno y otro no es la valoración, es la lectura clínica y el plan que te entregamos.",
    },
    {
      q: "Soy profesional de golf y tengo alumnos que no terminan de avanzar. ¿Podéis ayudarme como apoyo externo?",
      a: "Sí. Para profesionales de la enseñanza ofrecemos un programa de derivación: tú nos envías al alumno, nosotros valoramos sus límites físicos y mecánicos, y te devolvemos un informe claro con qué puede y qué no puede hacer su cuerpo. La idea no es sustituirte, sino darte criterio para decidir qué enseñar primero, qué dejar para más adelante y qué hay que entrenar físicamente antes de poder corregirlo técnicamente. Si te interesa, háblanos del perfil de tu alumnado y diseñamos el formato de colaboración que mejor encaje.",
    },
    {
      q: "¿Colaboráis con clubes de golf y academias?",
      a: "Sí, es una de nuestras líneas de trabajo. Planteamos acuerdos de colaboración con clubes y academias buscando objetivos comunes: jornadas de valoración para socios, programas de prevención para jugadores sénior, soporte científico al equipo técnico del club o eventos puntuales en torno a torneos. Cada acuerdo se diseña a medida, atendiendo al perfil de socios, las instalaciones disponibles y los objetivos deportivos del club. Si gestionas un club o academia y crees que puede encajar, escríbenos y concretamos una propuesta específica para vosotros.",
    },
    {
      q: "¿Qué pasa después del informe? ¿Qué opciones tengo para llevarlo a la práctica?",
      a: "El informe es tuyo y está pensado para que puedas darle continuidad de tres maneras. La primera, **trabajarlo de forma autónoma** siguiendo el plan de trabajo que te entregamos, con ejercicios pautados y objetivos por bloques. La segunda, **llegárselo a tu profesional de confianza** —preparador físico, fisioterapeuta o coach— para que lo integre en lo que ya esté haciendo contigo; el informe está redactado para que cualquier profesional cualificado pueda leerlo y trabajar a partir de él. Y la tercera, **continuar con nosotros**, ya sea con un seguimiento individual o dentro de uno de nuestros grupos de entrenamiento dirigidos. Eliges el formato que mejor encaje con tus objetivos, tu tiempo y tu presupuesto.",
    },
  ],
  contact: {
    eyebrow: "Reserva tu valoración",
    titleA: "Hablemos de ",
    titleEm: "tu juego",
    intro:
      "Cuéntanos tu nivel, tus objetivos y cualquier molestia o limitación que quieras comentar. Te responderemos en menos de 48h con disponibilidad de agenda y presupuesto.",
    emailLabel: "Email",
    whatsappLabel: "WhatsApp",
    locationLabel: "Ubicación",
    locationValue: "Albacete",
    mapTitle: "Mapa de Albacete",
    form: {
      name: "Nombre *",
      email: "Email",
      phone: "Teléfono *",
      handicap: "Hándicap",
      handicapPlaceholder: "Ej. 18,4 o ‘sin licencia’",
      playerProfile: "Perfil de jugador",
      playerProfilePlaceholder: "Selecciona tu perfil",
      profiles: {
        estancado: "Jugador estancado — quiero bajar mi hándicap",
        dolor: "Tengo dolor o molestias al jugar",
        competidor: "Competidor — busco rendimiento",
        profesional: "Profesional de la enseñanza o club / academia",
        otro: "Otro",
      },
      message: "Mensaje",
      messagePlaceholder: "Cuéntanos tu hándicap, objetivos y si quieres agendar algún día en concreto...",
      consentPre: "He leído y acepto la ",
      consentLink: "política de privacidad",
      consentPost: " y consiento el tratamiento de mis datos personales para gestionar mi solicitud.",
      submit: "Enviar mensaje",
      submitting: "Enviando...",
      errorIncomplete: "Por favor, indica al menos tu nombre y teléfono.",
      success: "Mensaje enviado. Te contactaremos en 24-48h.",
      errorSend: "Error al enviar el mensaje. Inténtalo de nuevo.",
    },
  },
  footer: {
    brandText:
      "Valoración integral del golfista conectando biomecánica, fuerza, rendimiento y control motor para decidir qué entrenar primero.",
    navTitle: "Navegación",
    contactTitle: "Contacto",
    legalTitle: "Legal",
    nav: { metodologia: "Metodología", tecnologia: "Tecnología", equipo: "Equipo", contacto: "Contacto" },
    legal: { privacidad: "Privacidad", aviso: "Aviso legal", cookies: "Cookies" },
    rights: "Todos los derechos reservados.",
  },
  cookies: {
    eyebrow: "Cookies",
    title: "Tu privacidad es importante",
    body: (
      <>
        Usamos únicamente cookies técnicas, imprescindibles para que el sitio funcione (sesión y
        preferencias internas). No usamos cookies analíticas, publicitarias ni de terceros.
      </>
    ),
    accept: "Aceptar",
    reject: "Rechazar",
    more: "Más información",
    ariaDialog: "Aviso de cookies",
    ariaClose: "Cerrar y rechazar cookies no necesarias",
    policyLink: "Política de Cookies",
  },
  whatsapp: { aria: "Escríbenos por WhatsApp" },
};

const en: Dict = {
  nav: {
    metodologia: "Methodology",
    tecnologia: "Technology",
    equipo: "Team",
    colaboradores: "Partners",
    testimonios: "Testimonials",
    faq: "FAQ",
    contacto: "Contact",
  },
  header: {
    adminPanel: "Admin panel",
    cta: "Book a session",
    menu: "Menu",
    home: "Golf Motion Lab — Home",
    langLabel: "Language",
  },
  hero: {
    badge: "Comprehensive golfer assessment",
    titleLine1: "We connect",
    titleLine2: "body and swing",
    titleLine3: "to improve your game.",
    paragraph: (
      <>
        We measure <strong className="text-[var(--forest-deep)]">mobility</strong>,{" "}
        <strong className="text-[var(--forest-deep)]">strength</strong>,{" "}
        <strong className="text-[var(--forest-deep)]">performance</strong> and{" "}
        <strong className="text-[var(--forest-deep)]">motor control</strong>. We turn the data into a
        comprehensive report scored from 0 to 100, plus a training plan that prioritises what is
        really holding your game back.
      </>
    ),
    ctaPrimary: "Book your assessment",
    ctaSecondary: "Explore the methodology",
    labChip: "Lab · 2026",
    heroImgAlt:
      "Golf Motion Lab studio with branded backdrop, TrackMan radar, iron and Titleist balls on a practice mat",
  },
  stats: [
    { num: "4", label: "Assessment pillars" },
    { num: "20+", label: "Metrics per report" },
    { num: "0 — 100", label: "Scoring system" },
    { num: "3", label: "Specialists per assessment: doctor, physio and coach" },
  ],
  methodology: {
    eyebrow: "Methodology",
    titleA: "Four pillars that ",
    titleEm: "shape your game",
    titleB: ".",
    intro:
      "Each pillar is scored out of 100 on a clinical scale. The global score is the weighted average of all four and tells you, at a single glance, where you stand and where you need to work.",
    imgAlt:
      "Golf Motion Lab biomechanical analysis: a player wearing body sensors with a 3D skeleton on screen showing pelvis, torso and X-Factor metrics",
    imgCaption: "Comprehensive assessment · Real lab session",
    processImgAlt: "The Golf Motion Lab team analysing the data from an assessment in the lab",
    processTitleA: "A ",
    processTitleEm: "three-step",
    processTitleB: " process.",
    processIntro:
      "Every assessment is teamwork: we combine the physiotherapist's, the doctor's and the coach's perspective so that no data point goes uninterpreted and the plan starts from a complete reading of your game.",
    steps: [
      {
        step: "01",
        title: "We assess",
        text: "A 90-minute lab session with a full battery of physical, biomechanical and impact tests.",
      },
      {
        step: "02",
        title: "We analyse",
        text: "We cross-reference the data from the 4 pillars and produce a visual report with a clear score and priorities.",
      },
      {
        step: "03",
        title: "We train",
        text: "An individualised training plan tailored to your weak points. You can work on it on your own, with your trusted professional or with our follow-up.",
      },
    ],
  },
  pillars: [
    {
      id: "movilidad",
      icon: Activity,
      number: "01",
      title: "Mobility",
      subtitle: "Does your body have the range it needs?",
      description:
        "We assess the joint ranges that are critical for the swing: thoracic rotation, hips, shoulders and ankles. We measure range, asymmetries and the quality of end-range control.",
      tags: ["Toe-Touch", "Thoracic Rotation", "Hip IR/ER", "Shoulder Mobility"],
    },
    {
      id: "fuerza",
      icon: Dumbbell,
      number: "02",
      title: "Strength",
      subtitle: "Can your body hold up and deliver speed?",
      description:
        "With the VALD Performance system we quantify maximal strength, power, side-to-side asymmetries and jump capacity. We turn numbers into trainable goals.",
      tags: ["Grip Strength", "CMJ", "Hamstring/Quad", "Asymmetries"],
    },
    {
      id: "rendimiento",
      icon: Zap,
      number: "03",
      title: "Performance",
      subtitle: "How does your impact behave?",
      description:
        "With TrackMan we analyse club speed, ball speed, smash factor, angle of attack and dispersion. We contrast the impact data against the physical data.",
      tags: ["Club Speed", "Ball Speed", "Smash Factor", "Dispersion"],
    },
    {
      id: "control",
      icon: Target,
      number: "04",
      title: "Motor control",
      subtitle: "Do you move well with what you have?",
      description:
        "With OnForm and 2D+3D video we assess kinematic sequencing, posture, balance, tempo and repeatability. This pillar separates the consistent player from the erratic one.",
      tags: ["Sequencing", "Posture", "Balance", "Tempo Ratio"],
    },
  ],
  technology: {
    eyebrow: "Clinical technology",
    titleA: "The same tools used by ",
    titleEm: "PGA Tour professionals",
  },
  tech: [
    {
      name: "VALD Performance",
      role: "Strength, power and asymmetries",
      description:
        "Clinical-grade dynamometers and force plates to measure how physical capacity transfers to the swing.",
    },
    {
      name: "TrackMan",
      role: "Impact and ball-flight data",
      description:
        "The PGA Tour reference doppler radar to quantify the efficiency of every shot with absolute objectivity.",
    },
    {
      name: "OnForm",
      role: "Video and sequence analysis",
      description:
        "2D and 3D analysis software with biomechanical measurements to break down swing quality phase by phase.",
    },
  ],
  team: {
    eyebrow: "Team",
    titleA: "Professionals who ",
    titleEm: "blend science and play",
    groupAlt: "Golf Motion Lab team: Sergio Sánchez, Aitor Pardo and Gaspar Plaza",
    followInstagram: "Follow me on Instagram",
    teaserIntro:
      "A doctor, a physiotherapist and a coach working on the same assessment. Meet the people behind every report.",
    teaserCta: "Meet the team",
    pageIntro:
      "Behind every assessment three disciplines come together: medical diagnosis, physical rehabilitation and technical swing analysis. These are the people who integrate them.",
    backHome: "Back to home",
  },
  quote: "\"A metric only makes the report if it changes a decision.\"",
  collaborators: {
    eyebrow: "Partners",
    title: "We work with those who set the standard.",
    intro:
      "We are backed by reference technology and top-level playing venues so that every assessment is rigorous and applicable.",
  },
  testimonialsSection: {
    eyebrow: "Testimonials",
    titleA: "What those who ",
    titleEm: "have already tried it say",
  },
  testimonials: [
    {
      name: "Rubén Pardo",
      role: "Professional golfer · ALPS Tour / PGA Spain",
      photo: "/img/testimonial-ruben-putt_4cc510a4.jpg",
      quote:
        "I was playing with constant lower-back pain, tournament after tournament, relying on anti-inflammatories just to compete. I went to see Sergio for a full assessment to understand what was going on, and we started working on it: load management, postural work and specific physical training. Nothing magic. The pain gradually faded and I became more aware of how I move. In the end that shows on the course.",
    },
    {
      name: "Juanjo Garrido",
      role: "Amateur · handicap 5.3",
      photo: "/img/testimonial-juanjo_795ba4c4.jpg",
      quote:
        "The lower-back pain was limiting me not only in golf, but in my everyday life. After the movement assessment I understood why it hurt. The plan was clear and so were the results. I was very consistent with what they prescribed and that pain is now behind me.",
    },
    {
      name: "Álvaro Calero",
      role: "Professional golfer · ALPS Tour / PGA Spain",
      photo: "/img/testimonial-alvaro-drive_e5cf6e12.jpg",
      quote:
        "Since I started working with Aitor I feel I approach competition in a completely different way. More prepared, more confident. He has a unique ability to make the difficult look simple and to help you see it that way too.",
    },
  ],
  faqSection: {
    eyebrow: "Frequently asked questions",
    titleA: "Before changing your swing,",
    titleEm: " understand your body.",
    intro:
      "We answer the most common questions from amateur players, golfers with recurring pain, competitors chasing marginal gains and professionals who want to outsource the physical assessment of their students.",
    helpBox:
      "Can't find your question here? Message us on WhatsApp or through the contact form and we'll reply within 48 hours with availability and pricing.",
    helpLink: "Go to the contact form",
  },
  faqs: [
    {
      q: "I've been stuck at the same handicap for years — what will change after an assessment with you?",
      a: "For most stuck players the ceiling isn't in technique, but in what the body allows or prevents the swing from doing. What comes out of the lab isn't a lesson but a map: two or three specific physical limits (for example short thoracic rotation, hip strength asymmetry or an uncoordinated sequence) and how they relate to your recurring mistakes. From there, you and your coach work on causes, not symptoms. That difference is usually what unlocks progress.",
    },
    {
      q: "I've been playing for a while with lower-back discomfort or golfer's elbow — is your assessment useful even if I'm already treating the pain?",
      a: "Yes, and in fact that's when it helps the most. Golf concentrates load on the lower back, elbow and shoulder through very repetitive movements; when it hurts, there is almost always a neighbouring area that isn't doing its job and is forcing another to overload. Our assessment does not replace medical or physiotherapy treatment: it complements it. We provide objective data on which structure is failing in the swing so your clinical team can direct the rehab with sports-specific criteria, not just symptom-based.",
    },
    {
      q: "I want to gain speed and distance. How much can a biomechanical analysis help me?",
      a: "Club speed is the result of a chain: how much force you can apply against the ground, how you transfer it through the pelvis and trunk, and how fast you reach impact without losing efficiency. We measure every link with TrackMan, VALD and video, and compare your numbers against sports references. That tells us whether your room for improvement is in strength, coordination, mobility or pure technique, and where to invest the coming weeks to gain real distance without paying for it with an injury.",
    },
    {
      q: "What exactly does the session include and what is the day like?",
      a: "Around 90 minutes in the lab split into four blocks: interview and review of your sporting history, physical assessment (mobility, strength and asymmetries with VALD), impact analysis with TrackMan and swing analysis with 2D+3D video in OnForm. Over the following days you receive the full report with your scores across the four pillars and a training plan prioritised by impact, tailored to your weak points.",
    },
    {
      q: "What do I need to bring and what playing level is required?",
      a: "You only need your mid iron (a 7-iron is perfect) and, if you'd also like to analyse the driver, bring it along. Wear comfortable sportswear you can run, jump and rotate in without restriction; for footwear, court or running shoes are best for the physical tests. We provide everything else. As for level: we work with beginners, club players, amateur competitors and professionals. What changes from one to another isn't the assessment, but the clinical reading and the plan we deliver.",
    },
    {
      q: "I'm a golf professional and I have students who can't quite progress. Can you help me as external support?",
      a: "Yes. For teaching professionals we offer a referral programme: you send us the student, we assess their physical and mechanical limits, and we return a clear report on what their body can and cannot do. The idea is not to replace you, but to give you criteria to decide what to teach first, what to leave for later and what needs to be trained physically before it can be corrected technically. If you're interested, tell us about your students' profile and we'll design the collaboration format that fits best.",
    },
    {
      q: "Do you work with golf clubs and academies?",
      a: "Yes, it's one of our lines of work. We propose collaboration agreements with clubs and academies around shared goals: assessment days for members, prevention programmes for senior players, scientific support for the club's technical team or one-off events around tournaments. Each agreement is designed to measure, considering the member profile, the available facilities and the club's sporting goals. If you manage a club or academy and think it could be a fit, write to us and we'll put together a specific proposal for you.",
    },
    {
      q: "What happens after the report? What options do I have to put it into practice?",
      a: "The report is yours and is designed so you can follow it up in three ways. First, **work on it on your own** following the training plan we deliver, with prescribed exercises and goals by block. Second, **take it to your trusted professional** —strength coach, physiotherapist or coach— so they can integrate it into what they're already doing with you; the report is written so any qualified professional can read it and work from it. And third, **continue with us**, either with individual follow-up or within one of our guided training groups. You choose the format that best fits your goals, your time and your budget.",
    },
  ],
  contact: {
    eyebrow: "Book your assessment",
    titleA: "Let's talk about ",
    titleEm: "your game",
    intro:
      "Tell us your level, your goals and any discomfort or limitation you'd like to mention. We'll get back to you within 48h with availability and a quote.",
    emailLabel: "Email",
    whatsappLabel: "WhatsApp",
    locationLabel: "Location",
    locationValue: "Albacete",
    mapTitle: "Map of Albacete",
    form: {
      name: "Name *",
      email: "Email",
      phone: "Phone *",
      handicap: "Handicap",
      handicapPlaceholder: "e.g. 18.4 or ‘no licence’",
      playerProfile: "Player profile",
      playerProfilePlaceholder: "Select your profile",
      profiles: {
        estancado: "Stuck player — I want to lower my handicap",
        dolor: "I have pain or discomfort when playing",
        competidor: "Competitor — chasing performance",
        profesional: "Teaching professional or club / academy",
        otro: "Other",
      },
      message: "Message",
      messagePlaceholder: "Tell us your handicap, goals and whether you'd like to book a specific day...",
      consentPre: "I have read and accept the ",
      consentLink: "privacy policy",
      consentPost: " and I consent to the processing of my personal data to handle my request.",
      submit: "Send message",
      submitting: "Sending...",
      errorIncomplete: "Please provide at least your name and phone number.",
      success: "Message sent. We'll get back to you within 24-48h.",
      errorSend: "Error sending the message. Please try again.",
    },
  },
  footer: {
    brandText:
      "Comprehensive golfer assessment connecting biomechanics, strength, performance and motor control to decide what to train first.",
    navTitle: "Navigation",
    contactTitle: "Contact",
    legalTitle: "Legal",
    nav: { metodologia: "Methodology", tecnologia: "Technology", equipo: "Team", contacto: "Contact" },
    legal: { privacidad: "Privacy", aviso: "Legal notice", cookies: "Cookies" },
    rights: "All rights reserved.",
  },
  cookies: {
    eyebrow: "Cookies",
    title: "Your privacy matters",
    body: (
      <>
        We use only technical cookies, strictly necessary for the site to work (session and internal
        preferences). We do not use analytics, advertising or third-party cookies.
      </>
    ),
    accept: "Accept",
    reject: "Reject",
    more: "More information",
    ariaDialog: "Cookie notice",
    ariaClose: "Close and reject non-essential cookies",
    policyLink: "Cookie Policy",
  },
  whatsapp: { aria: "Message us on WhatsApp" },
};

const dictionaries: Record<Lang, Dict> = { es, en };

export function getDict(lang: Lang): Dict {
  return dictionaries[lang];
}

/**
 * English overrides for the team bios that live in the database (keyed by coach name).
 * The Spanish bio comes from the DB; when the UI is in English we swap to these.
 */
export const coachBiosEn: Record<string, { title: string; bio: string }> = {
  "Gaspar Plaza": {
    title: "Orthopaedic Surgeon · Sports Medicine",
    bio: "Orthopaedic and trauma surgeon specialised in Orthopaedic Surgery and Traumatology and in Sports Science, with 15 years of experience in sports medicine, arthroscopic techniques and advanced biological therapies. His international training includes fellowships in the United States and robotic-assisted surgery. He is a physician in the Knee Unit at CHUA, a lecturer at the Albacete Faculty of Medicine and a teaching collaborator in the UCAM Surgery chair. At Golf Motion Lab he brings the medical criteria that backs every assessment: a precise diagnosis of the injury and its origin, so that the training plan always starts from a body that is well understood and protects your health out on the course.",
  },
  "Sergio Sánchez": {
    title: "Physiotherapist · Rehab & Performance",
    bio: "Physiotherapist specialised in rehabilitation and performance, with more than 10 years of experience in high-performance sports settings. He has been part of team coaching staff, working on rehabilitation, recovery and performance with elite athletes. His international training spans movement biomechanics, strength training and exercise prescription, motor control and load management. At Golf Motion Lab he brings all that experience from professional sport to golf: assessing the body with precision, identifying what is limiting your swing and designing a plan that turns data into real improvements on the course.",
  },
  "Aitor Pardo": {
    title: "Technical Director · Swing Specialist",
    bio: "Professional player and Level 1 Sports Coach certified by the Royal Spanish Golf Federation. TPI Certified and Professional Clubfitter, he serves as Technical Director of the Castilla-La Mancha Golf Federation. He coaches professional players competing on the Alps Tour, the Challenge Tour and PGA Spain. At Golf Motion Lab he brings expert analysis of swing kinematics, studying every detail of the sequence and translating the lab data into technical decisions the player can apply from the very first session.",
  },
};
